<template>
	<div v-if="photos" ref="el" data-component="photos" class="paused">
		<div class="inner">
			<figure v-for="(item, i) in photos" :key="i" class="photo-container">
				<ImageAsset :asset="item.photo" sizes="xsmall:300px medium:400px xlarge:500px" />
				<figcaption class="-callout -uppercase title">{{ item.title }}</figcaption>
			</figure>
		</div>

		<div class="inner">
			<figure v-for="(item, i) in photos" :key="i" class="photo-container">
				<ImageAsset :asset="item.photo" sizes="xsmall:300px medium:400px xlarge:500px" />
				<figcaption class="-callout -uppercase title">{{ item.title }}</figcaption>
			</figure>
		</div>
	</div>
</template>

<script setup>
import { gsap, ScrollTrigger } from 'gsap/all'
import { ImageAsset } from '~/components/core'

import.meta.client && gsap.registerPlugin(ScrollTrigger)

const context = inject('pageContext')
const el = ref(null)
let observer

defineProps({
	photos: { type: [Boolean, Array], default: () => [] },
})

const setup = () => {
	observer = ScrollTrigger.create({
		trigger: el.value,
		onEnter: () => el.value.classList.remove('paused'),
		onEnterBack: () => el.value.classList.remove('paused'),
		onLeave: () => el.value.classList.add('paused'),
		onLeaveBack: () => el.value.classList.add('paused'),
	})
}

const kill = () => {
	observer?.kill()
}

onBeforeUnmount(() => kill())

onMounted(() => {
	context.$page.loader.ready.then(() => setup())
})
</script>

<style lang="scss" scoped>
[data-component='photos'] {
	position: relative;

	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-start;
	width: 100%;
	height: 100%;

	overflow: hidden;

	@include max-screen($small) {
		flex-direction: row;
	}

	&.paused {
		.inner {
			animation-play-state: paused;
		}
	}

	.inner {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		width: 100%;

		animation-name: loop;
		animation-iteration-count: infinite;
		animation-play-state: running;
		animation-duration: 22s;
		animation-timing-function: linear;

		@include max-screen($small) {
			animation-name: loop-horizontal;
			flex-direction: row;
			width: max-content;
			height: 100%;
		}

		@keyframes loop-horizontal {
			0% {
				transform: translate3d(-100%, 0, 0);
			}
			100% {
				transform: translate3d(0, 0, 0);
			}
		}

		@keyframes loop {
			0% {
				transform: translate3d(0, 0, 0);
			}
			100% {
				transform: translate3d(0, -100%, 0);
			}
		}
	}

	.photo-container {
		position: relative;

		width: 100%;
		height: rs(320);

		@include max-screen($small) {
			width: n_grid_columns(8);
			flex: 0 0 n_grid_columns(8);
			height: 100%;
		}

		@include max-screen($xsmall) {
			width: n_grid_columns(13);
			flex: 0 0 n_grid_columns(13);
		}

		&:after {
			content: '';
			position: absolute;
			inset: 0;

			display: block;
			background-color: var(--color-black);
			opacity: 0.2;
		}

		img {
			height: 100%;
			width: 100%;
			object-fit: cover;

			@include max-screen($small) {
				height: 100%;
				width: auto;
			}
		}

		.title {
			position: absolute;
			bottom: rs(28);
			left: rs(24);

			color: var(--color-white);

			z-index: 1;
		}
	}
}
</style>
